import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Route, Router, Switch, useLocation } from "wouter";
import { AppShell } from "./components/AppShell.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary.jsx";
import { IdleGuard } from "./components/IdleGuard.jsx";
import { OfflineProvider } from "./components/OfflineProvider.jsx";
import { RotateNotice } from "./components/RotateNotice.jsx";
import { SigningReturn } from "./components/SigningReturn.jsx";
import { getToken, SESSION_ENDED } from "./lib/api.js";
import { clearLocked, isLocked } from "./lib/idle-lock.js";
import { lazyRoute } from "./lib/lazy-route.js";
import { lockSession } from "./lib/lock-session.js";
import { refreshOnRestore } from "./lib/page-lifecycle.js";
import { hasIntent } from "./lib/signing.js";
import { LockScreen } from "./routes/LockScreen.jsx";
import { MyWork } from "./routes/MyWork.jsx";
import { SignIn } from "./routes/SignIn.jsx";
import "./styles/stitch.css";
import "./styles/tokens.css";
import "./styles/app.css";

/**
 * Routes are loaded when first visited (PERF-001 bundle budget). The sign-in screen, the shell and the operator's
 * work list stay in the entry chunk because every session starts there; the office screens (reports and charts,
 * master data, the audit-trail workbench, MBR authoring) are the bulk of the code and most operators never open them.
 */
const Analytics = lazyRoute(() => import("./routes/Analytics.jsx"), "Analytics");
const AlarmLog = lazyRoute(() => import("./routes/AlarmLog.jsx"), "AlarmLog");
const AuditTrail = lazyRoute(() => import("./routes/AuditTrail.jsx"), "AuditTrail");
const BatchDashboard = lazyRoute(() => import("./routes/BatchDashboard.jsx"), "BatchDashboard");
const BatchProcessData = lazyRoute(() => import("./routes/BatchProcessData.jsx"), "BatchProcessData");
const BatchOverview = lazyRoute(() => import("./routes/BatchOverview.jsx"), "BatchOverview");
const BatchRecipe = lazyRoute(() => import("./routes/BatchRecipe.jsx"), "BatchRecipe");
const BatchValues = lazyRoute(() => import("./routes/BatchValues.jsx"), "BatchValues");
const ControlFactory = lazyRoute(() => import("./routes/ControlFactory.jsx"), "ControlFactory");
const ControlInterlocks = lazyRoute(() => import("./routes/ControlInterlocks.jsx"), "ControlInterlocks");
const ControlModules = lazyRoute(() => import("./routes/ControlModules.jsx"), "ControlModules");
const ControlPlant = lazyRoute(() => import("./routes/ControlPlant.jsx"), "ControlPlant");
const ControlReleases = lazyRoute(() => import("./routes/ControlReleases.jsx"), "ControlReleases");
const ControlSimulation = lazyRoute(() => import("./routes/ControlSimulation.jsx"), "ControlSimulation");
const ControlAlarms = lazyRoute(() => import("./routes/ControlAlarms.jsx"), "ControlAlarms");
const ControlAlarmWorkbench = lazyRoute(
  () => import("./routes/ControlAlarmWorkbench.jsx"),
  "ControlAlarmWorkbench",
);
const ControlFaceplate = lazyRoute<{ tag: string }, "ControlFaceplate">(
  () => import("./routes/ControlFaceplate.jsx"),
  "ControlFaceplate",
);
const ControlEnterprise = lazyRoute(() => import("./routes/ControlEnterprise.jsx"), "ControlEnterprise");
const ControlHealth = lazyRoute(() => import("./routes/ControlHealth.jsx"), "ControlHealth");
const ControlOverview = lazyRoute(() => import("./routes/ControlOverview.jsx"), "ControlOverview");
const ControlTags = lazyRoute(() => import("./routes/ControlTags.jsx"), "ControlTags");
const ControlTrends = lazyRoute(() => import("./routes/ControlTrends.jsx"), "ControlTrends");
const DispenseWorklist = lazyRoute(() => import("./routes/DispenseWorklist.jsx"), "DispenseWorklist");
const BatchTicket = lazyRoute(() => import("./routes/BatchTicket.jsx"), "BatchTicket");
const ProductionBoard = lazyRoute(() => import("./routes/ProductionBoard.jsx"), "ProductionBoard");
const DispenseList = lazyRoute(() => import("./routes/DispenseList.jsx"), "DispenseList");
const DispositionAndRelease = lazyRoute(
  () => import("./routes/DispositionAndRelease.jsx"),
  "DispositionAndRelease",
);
const ExceptionReview = lazyRoute(() => import("./routes/ExceptionReview.jsx"), "ExceptionReview");
const ExceptionDashboard = lazyRoute(() => import("./routes/ExceptionDashboard.jsx"), "ExceptionDashboard");
const ExceptionOnlyReview = lazyRoute(
  () => import("./routes/ExceptionOnlyReview.jsx"),
  "ExceptionOnlyReview",
);
const MbrDesigner = lazyRoute(() => import("./routes/MbrDesigner.jsx"), "MbrDesigner");
const MasterBatchRecords = lazyRoute(() => import("./routes/MasterBatchRecords.jsx"), "MasterBatchRecords");
const ControlUnit = lazyRoute<{ unitId: string }, "ControlUnit">(
  () => import("./routes/ControlUnit.jsx"),
  "ControlUnit",
);
const SetupStart = lazyRoute(() => import("./routes/SetupStart.jsx"), "SetupStart");
const SetupColumns = lazyRoute(() => import("./routes/SetupColumns.jsx"), "SetupColumns");
const SetupReview = lazyRoute(() => import("./routes/SetupReview.jsx"), "SetupReview");
const SetupCheck = lazyRoute(() => import("./routes/SetupCheck.jsx"), "SetupCheck");
const SetupSimulate = lazyRoute(() => import("./routes/SetupSimulate.jsx"), "SetupSimulate");
const SetupRelease = lazyRoute(() => import("./routes/SetupRelease.jsx"), "SetupRelease");
const EquipmentStatus = lazyRoute(() => import("./routes/EquipmentStatus.jsx"), "EquipmentStatus");
const SiteModel = lazyRoute(() => import("./routes/SiteModel.jsx"), "SiteModel");
const MasterDataChanges = lazyRoute(() => import("./routes/MasterDataChanges.jsx"), "MasterDataChanges");
const MaterialsLots = lazyRoute(() => import("./routes/MaterialsLots.jsx"), "MaterialsLots");
const PeopleTraining = lazyRoute(() => import("./routes/PeopleTraining.jsx"), "PeopleTraining");
const MasterData = lazyRoute(() => import("./routes/MasterData.jsx"), "MasterData");
const OfflineConflicts = lazyRoute(() => import("./routes/OfflineConflicts.jsx"), "OfflineConflicts");
const OfflineQueue = lazyRoute(() => import("./routes/OfflineQueue.jsx"), "OfflineQueue");
const NotFound = lazyRoute(() => import("./routes/NotFound.jsx"), "NotFound");
const PrintExport = lazyRoute<{ batchId: string }, "PrintExport">(
  () => import("./routes/PrintExport.jsx"),
  "PrintExport",
);
const ProcessParameters = lazyRoute(() => import("./routes/ProcessParameters.jsx"), "ProcessParameters");
const QpCertification = lazyRoute(() => import("./routes/QpCertification.jsx"), "QpCertification");
const Genealogy = lazyRoute(() => import("./routes/Genealogy.jsx"), "Genealogy");
const Reconciliation = lazyRoute(() => import("./routes/Reconciliation.jsx"), "Reconciliation");
const Reports = lazyRoute(() => import("./routes/Reports.jsx"), "Reports");
const StepExecution = lazyRoute(() => import("./routes/StepExecution.jsx"), "StepExecution");
const WeighAndDispense = lazyRoute(() => import("./routes/WeighAndDispense.jsx"), "WeighAndDispense");

const client = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 5_000 } } });

/**
 * Where this client is mounted. Staging serves it under /ebr, so both the asset URLs (Vite's `base`) and
 * the router have to know that prefix — otherwise every route matches the wrong path and the app renders
 * its fallback on every page. Vite exposes the same value it used at build time.
 */
const BASE_PATH = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");

/**
 * The shell and the record area, both inside the router.
 *
 * Both, deliberately. The module rail's links used to be rendered outside it, so they navigated to "/mbr"
 * rather than "/ebr/mbr" — and once the location no longer started with the router's base, wouter matched no
 * route and rendered nothing at all. Not the fallback: nothing. That is a blank page beside an intact rail,
 * with no error anywhere, and it is what every click on the menu produced.
 *
 * It survived because the stories navigated by URL, which builds the prefix for them. Clicking is what a
 * person does, so clicking is what the stories do now.
 */
function RouteLoading() {
  return (
    <p role="status" aria-live="polite" className="route-loading">
      Loading…
    </p>
  );
}

function Shell() {
  // wouter's location, already stripped of the base — and it updates on navigation, which reading
  // window.location during render did not.
  const [location] = useLocation();
  const path = location.startsWith("/batches") ? "/" : location;

  return (
    <AppShell path={path} location={location}>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
          <Switch>
            <Route path="/control/setup/columns">
              <SetupColumns />
            </Route>
            <Route path="/control/setup/review">
              <SetupReview />
            </Route>
            <Route path="/control/setup/check">
              <SetupCheck />
            </Route>
            <Route path="/control/setup/simulate">
              <SetupSimulate />
            </Route>
            <Route path="/control/setup/release">
              <SetupRelease />
            </Route>
            <Route path="/control/setup">
              <SetupStart />
            </Route>
            <Route path="/control/factory">
              <ControlFactory />
            </Route>
            <Route path="/control/units/:id">{(p) => <ControlUnit unitId={p.id as string} />}</Route>
            <Route path="/dispensing">
              <DispenseWorklist />
            </Route>
            <Route path="/board">
              <ProductionBoard />
            </Route>
            <Route path="/dashboard">
              <BatchDashboard />
            </Route>
            <Route path="/mbr/:id/designer">{(p) => <MbrDesigner templateId={p.id as string} />}</Route>
            <Route path="/mbr">
              <MasterBatchRecords />
            </Route>
            <Route path="/reference/limits">
              <ProcessParameters />
            </Route>
            <Route path="/control/modules/:tag">{(p) => <ControlFaceplate tag={p.tag as string} />}</Route>
            <Route path="/control/alarms/manage">
              <ControlAlarmWorkbench />
            </Route>
            <Route path="/control/alarms">
              <ControlAlarms />
            </Route>
            <Route path="/control/trends">
              <ControlTrends />
            </Route>
            <Route path="/control/tags">
              <ControlTags />
            </Route>
            <Route path="/control/health">
              <ControlHealth />
            </Route>
            <Route path="/control/enterprise">
              <ControlEnterprise />
            </Route>
            <Route path="/control">
              <ControlOverview />
            </Route>
            <Route path="/master-data">
              <MasterData />
            </Route>
            <Route path="/master-data/materials">
              <MaterialsLots />
            </Route>
            <Route path="/master-data/equipment">
              <EquipmentStatus />
            </Route>
            <Route path="/master-data/site">
              <SiteModel />
            </Route>
            <Route path="/master-data/people">
              <PeopleTraining />
            </Route>
            <Route path="/master-data/changes">
              <MasterDataChanges />
            </Route>
            <Route path="/offline">
              <OfflineQueue />
            </Route>
            <Route path="/batches/:id/offline-conflicts">
              {(p) => <OfflineConflicts batchId={p.id as string} />}
            </Route>
            <Route path="/audit-trail">
              <AuditTrail />
            </Route>
            <Route path="/alarms">
              <AlarmLog />
            </Route>
            <Route path="/control/engineering/modules">
              <ControlModules />
            </Route>
            <Route path="/control/engineering/releases">
              <ControlReleases />
            </Route>
            <Route path="/control/engineering/plant">
              <ControlPlant />
            </Route>
            <Route path="/control/interlocks">
              <ControlInterlocks />
            </Route>
            <Route path="/control/simulation">
              <ControlSimulation />
            </Route>
            <Route path="/reports">
              <Reports />
            </Route>
            <Route path="/analytics">
              <Analytics />
            </Route>
            <Route path="/batches/:id/process">{(p) => <BatchProcessData batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/execution">{(p) => <StepExecution batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/values">{(p) => <BatchValues batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/recipe">{(p) => <BatchRecipe batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/dispense/:orderId/reconciliation">
              {(p) => <Reconciliation batchId={p.id as string} orderId={p.orderId as string} />}
            </Route>
            <Route path="/batches/:id/dispense/:orderId">
              {(p) => <WeighAndDispense batchId={p.id as string} orderId={p.orderId as string} />}
            </Route>
            <Route path="/batches/:id/dispense">{(p) => <DispenseList batchId={p.id as string} />}</Route>
            <Route path="/exceptions">
              <ExceptionDashboard />
            </Route>
            <Route path="/batches/:id/exception-report">
              {(p) => <ExceptionOnlyReview batchId={p.id as string} />}
            </Route>
            <Route path="/batches/:id/review">{(p) => <ExceptionReview batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/release">
              {(p) => <DispositionAndRelease batchId={p.id as string} />}
            </Route>
            <Route path="/batches/:id/genealogy">{(p) => <Genealogy batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/ticket">{(p) => <BatchTicket batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/print">{(p) => <PrintExport batchId={p.id as string} />}</Route>
            {/* the QR code printed on every PDF resolves here (PRT-014) */}
            <Route path="/office/batches/:id/print">{(p) => <PrintExport batchId={p.id as string} />}</Route>
            <Route path="/batches/:id/certification">
              {(p) => <QpCertification batchId={p.id as string} />}
            </Route>
            <Route path="/batches/:id">{(p) => <BatchOverview batchId={p.id as string} />}</Route>
            <Route path="/">
              <MyWork />
            </Route>
            <Route>{() => <NotFound path={window.location.pathname} />}</Route>
          </Switch>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

function App() {
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  // When the server stops accepting the session, come back here rather than leaving every screen showing a
  // refusal the person cannot act on. The queries are dropped with it: none of their answers are still true.
  useEffect(() => {
    const ended = () => {
      client.clear();
      // the updater form, so the listener captures nothing that changes between renders
      force((n) => n + 1);
    };
    window.addEventListener(SESSION_ENDED, ended);
    return () => window.removeEventListener(SESSION_ENDED, ended);
  }, []);

  // ERR-010: a page restored by the back button shows what the server says now, not what it said when left
  useEffect(() => refreshOnRestore(window, () => void client.invalidateQueries()), []);

  const onLock = useCallback(() => void lockSession(), []);
  const returning = /[?&](code|error)=/.test(window.location.search);
  // SEC-014: after an inactivity lock nothing is shown, and the way back is a fresh authentication
  if (!getToken() && isLocked(sessionStorage) && !returning) return <LockScreen />;
  if (!getToken())
    return (
      <SignIn
        onSignedIn={() => {
          clearLocked(sessionStorage);
          rerender();
        }}
      />
    );
  // Back from the provider's re-authentication for a signature (§5.6): finish it before showing anything else.
  if (hasIntent(sessionStorage) && /[?&](code|error)=/.test(window.location.search))
    return <SigningReturn onDone={rerender} />;
  return (
    <Router base={BASE_PATH}>
      <IdleGuard onLock={onLock} />
      <OfflineProvider>
        <Shell />
      </OfflineProvider>
    </Router>
  );
}

// OFF-001: let the application open with no connection, so a queue on the device can always be reached
if ("serviceWorker" in navigator && import.meta.env.PROD)
  void navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch(() => undefined);

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <RotateNotice />
    <QueryClientProvider client={client}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
